
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance, defineComponent } from 'vue';
import Modal from '@/components/ModalAliare.vue';
import ModalButtonSuccess from '@/components/ModalButtonSuccess.vue';
import ModalButtonCancel from '@/components/ModalButtonCancel.vue';
import CardInfo from '@/components/CardInfo.vue';
import CardFlow from '@/components/CardFlow.vue';
import AliareSelect from '@/components/AliareSelect.vue';
import AliareInput from '@/components/AliareInput.vue';
import SelectTree from '@/components/SelectTree.vue';
import ScrollInfinite from '@/components/ScrollInfinite.vue';
import Loading from '@/components/Loading.vue';

import { useProjectPermission } from '@/composables/useProjectPermission';
import { useGenericHelpers } from '@/composables/useGenericHelpers';

import WebStorage from '@/common/WebStorage';
import { setupHeaderAliareTenant } from '@/config/axiosConfig';

export default defineComponent({
	name: 'Project',
	components: {
		Modal,
		CardInfo,
		CardFlow,
		ModalButtonSuccess,
		ModalButtonCancel,
		AliareSelect,
		AliareInput,
		SelectTree,
		ScrollInfinite,
		Loading
	},
	setup() {
		const instance = getCurrentInstance();
		const proxy = instance?.proxy as any;
		const store = proxy?.$store;
		const router = proxy?.$router;
		const root = proxy?.$root;

		// Composables
		const projectPermission = useProjectPermission();
		const genericHelpers = useGenericHelpers();

		// Data
		const value = ref(null);
		const selectedValue = ref('2');
		const message = ref('');
		const account = ref<any[]>([]);
		const optionList = ref([
			{
				id: 1,
				label: 'Todos',
			},
			{
				id: 2,
				label: 'Ativo',
			},
			{
				id: 3,
				label: 'Inativo',
			},
		]);

		const resultOptions = ref([
			{
				id: 1,
				label: 'Todos os Resultados',
			},
			{
				id: 2,
				label: 'Resultados com Sucesso',
			},
			{
				id: 3,
				label: 'Resultados com Alerta',
			},
			{
				id: 4,
				label: 'Resultados com Falha',
			},
		]);

		const pageSize = ref(15);
		const page = ref(1);
		const scrollInfinite = ref(true);
		const resultDetails = ref<any>('');
		const dataTotalizers = ref<any[]>([]);
		const dataTenant = ref<any[]>([]);
		const intervalTimer = ref<any>(null);
		const status = ref('active');
		const statusExecution = ref('all');
		const tokenData = ref('');
		const isLoading = ref(false);
		const isLoadingCards = ref(false);

		// Computed
		const organization = computed(() => JSON.parse(WebStorage.getItem('cardOrganization') || '[]'));
		const accountInfo = computed(() => JSON.parse(WebStorage.getItem('accountInfo') || '{}'));
		const accountName = computed(() => WebStorage.getItem('accountName') || '');
		const workspace = computed(() => JSON.parse(WebStorage.getItem('workspace') || '{}'));

		// Actions from store
		const getProjectDetails = (payload: any) => store?.dispatch('project/getProjectDetails', payload);
		const getProjectTotalizers = (payload: any) => store?.dispatch('project/getProjectTotalizers', payload);

		// Methods
		const getInformations = async () => {
			const isFirstTime = WebStorage.getItem('isFirstTime');

			if (!Object.keys(workspace.value).length) {
				router?.push({
					name: 'workspace',
				});
				return;
			}

			if (isFirstTime === null) {
				isLoading.value = true;
				isLoadingCards.value = true;
				await Promise.all([getAccount(), totalizers()]);
				isLoadingCards.value = false;
				isLoading.value = false;

				WebStorage.setItem('isFirstTime', 'false');
			} else {
				isLoadingCards.value = true;
				await Promise.all([getAccount(), totalizers()]);
				isLoadingCards.value = false;
			}
		};

		const paginations = async () => {
			if (!scrollInfinite.value) {
				return;
			}

			page.value++;

			let payload: any = null;

			switch (statusExecution.value) {
				case 'all':
					payload = { workspaceId: workspace.value.id, page: page.value, size: pageSize.value };
					break;
				case 'success':
					payload = { workspaceId: workspace.value.id, page: page.value, size: pageSize.value, resultadoExecucao: 'Sucesso' };
					break;
				case 'alert':
					payload = { workspaceId: workspace.value.id, page: page.value, size: pageSize.value, resultadoExecucao: 'Alerta' };
					break;
				case 'fail':
					payload = { workspaceId: workspace.value.id, page: page.value, size: pageSize.value, resultadoExecucao: 'Falha' };
					break;
				default:
					break;
			}

			switch (status.value) {
				case 'active':
					payload.ativo = true;
					break;
				case 'inactive':
					payload.ativo = false;
					break;
				default:
					break;
			}

			try {
				const response = await getProjectDetails(payload);

				if (!response?.data?.length) {
					scrollInfinite.value = false;
					page.value = 1;
					pageSize.value = 15;
					return;
				}
				const projectInfinite = response.data;

				resultDetails.value.data.push(...projectInfinite);
			} catch (error) {
				console.error(error);
				scrollInfinite.value = false;
			}
		};

		const totalizers = () => {
			const tenantId = WebStorage.getItem('tenantId');
			setupHeaderAliareTenant(tenantId);

			getProjectTotalizers({workspaceId: workspace.value.id}).then((resultTotalizers: any) => {
				dataTotalizers.value = resultTotalizers.data;
			});
		};

		const onStatus = async (type: any) => {
			isLoadingCards.value = true;
			const tenantId = WebStorage.getItem('tenantId');
			proxy?.$analytics.resourceManaged('Projeto', 'Filtro status');
			setupHeaderAliareTenant(tenantId);
			resultDetails.value = '';
			let response: any = [];
			page.value = 1;

			try {
				const resultadoExecucao = statusExecution.value == 'all' ? '' : statusExecution.value == 'success' ? 'Sucesso' : statusExecution.value == 'alert' ? 'Alerta' : statusExecution.value == 'fail' ? 'Falha' : '';

				switch (type.id) {
					case 1:
						status.value = 'all';
						response = await getProjectDetails({workspaceId: workspace.value.id, page: 1, size: pageSize.value, resultadoExecucao });
						break;
					case 2:
						status.value = 'active';
						response = await getProjectDetails({workspaceId: workspace.value.id, page: 1, size: pageSize.value, ativo: true, resultadoExecucao });
						break;
					case 3:
						status.value = 'inactive';
						response = await getProjectDetails({workspaceId: workspace.value.id, page: 1, size: pageSize.value, ativo: false });
						break;
					default:
						break;
				}

				if (response.data.length == 0) {
					scrollInfinite.value = false;
				} else {
					scrollInfinite.value = true;
				}

				resultDetails.value = response;

			} catch (error) {
				console.log('error', error);
			}

			isLoadingCards.value = false;
		};

		const onResult = async (type: any) => {
			isLoadingCards.value = true;

			const tenantId = WebStorage.getItem('tenantId');
			setupHeaderAliareTenant(tenantId);
			resultDetails.value = '';
			let response: any = [];
			page.value = 1;

			const statusProjeto = status.value == 'all' ? '' : status.value == 'active' ? true : status.value == 'inactive' ? false : '';

			try {
				switch (type.id) {
					case 1:
						statusExecution.value = 'all';
						response = await getProjectDetails({workspaceId: workspace.value.id, page: 1, size: pageSize.value, ativo: statusProjeto});
						break;
					case 2:
						statusExecution.value = 'success';
						response = await getProjectDetails({workspaceId: workspace.value.id, page: 1, size: pageSize.value, resultadoExecucao: 'Sucesso', ativo: statusProjeto });
						break;
					case 3:
						statusExecution.value = 'alert';
						response = await getProjectDetails({workspaceId: workspace.value.id, page: 1, size: pageSize.value, resultadoExecucao: 'Alerta', ativo: statusProjeto });
						break;
					case 4:
						statusExecution.value = 'fail';
						response = await getProjectDetails({workspaceId: workspace.value.id, page: 1, size: pageSize.value, resultadoExecucao: 'Falha', ativo: statusProjeto });
						break;
					default:
						break;
				}

				resultDetails.value = response;
				scrollInfinite.value = true;
			} catch (error) {
				console.log('error', error);
			}

			isLoadingCards.value = false;
		};

		const getAccount = async (sizeInfinite?: boolean) => {
			if (sizeInfinite) {
				pageSize.value = 99999;
				scrollInfinite.value = false;
			}
			const statusValue = status.value == 'all' ? '' : status.value == 'active' ? true : false;
			const tenantId = WebStorage.getItem('tenantId');
			setupHeaderAliareTenant(tenantId);
			page.value = 1;

			await getProjectDetails({workspaceId: workspace.value.id, page: page.value, size: pageSize.value, ativo: statusValue })
			.then((responseAccount: any) => {
				if (sizeInfinite) {
					responseAccount.data.forEach((project: any) => {
						if (project.permissao == 'Editar') {
							project.canShare = projectPermission.canShareProject(project);
							project.selectedProject = true;
						}
					});
					resultDetails.value = '';
					resultDetails.value = responseAccount;
					status.value = 'active';

				} else {
					scrollInfinite.value = true;
					resultDetails.value = responseAccount;
					status.value = 'active';
				}

			}).catch((error: any) => {
				const ErrorProject = error.response?.data?.notifications;
				store?.commit('activeAlert', {
					message: 'Ocorreu um erro ao tentar carregar a página de Projetos. Por favor tente novamente',
					status: 'fail',
				});
				console.error(ErrorProject);
			});

			pageSize.value = 15;
		};

		const eventHandler = async () => {
			isLoadingCards.value = true;
			resultDetails.value = '';
			status.value = 'active';
			statusExecution.value = 'all';
			await getAccount();
			isLoadingCards.value = false;
		};

		const showModals = (key: string) => {
			if (key === 'createEditProject') {
				store?.dispatch('ui/openCreateEditProjectModal', { mode: 'create' });
				proxy?.$analytics.resourceManaged('Projeto', 'Criar Projeto');
			}
		};

		// Lifecycle hooks
		onMounted(async () => {
			tokenData.value = WebStorage.getItem('tokenData') || '';
			await getInformations();

			intervalTimer.value = window.setInterval(() => {
				getAccount();
			}, 300000);

			genericHelpers.initializeTrackSale();

			proxy?.$analytics.functionalityAccessed('Projetos');

			root.$on("reloadProject", eventHandler);
		});

		onBeforeUnmount(() => {
			if (intervalTimer.value !== null) {
				clearInterval(intervalTimer.value);
			}
			root.$off("reloadProject", eventHandler);
		});

		return {
			// Data
			value,
			selectedValue,
			message,
			account,
			optionList,
			resultOptions,
			pageSize,
			page,
			scrollInfinite,
			resultDetails,
			dataTotalizers,
			dataTenant,
			intervalTimer,
			status,
			statusExecution,
			tokenData,
			isLoading,
			isLoadingCards,
			// Computed
			organization,
			accountInfo,
			accountName,
			workspace,
			// Methods
			getInformations,
			paginations,
			totalizers,
			onStatus,
			onResult,
			getAccount,
			eventHandler,
			showModals,
			// From composables
			...projectPermission,
			...genericHelpers,
		};
	},
});
